import { normaliseForSearch } from "@thalamos/common";

/**
 * A reusable searcher over a fixed list. Call the returned function with each
 * query (e.g. on every keystroke).
 */
export type PlaceSearcher<T> = (query: string, limit: number) => T[];

type Indexed<T> = { item: T; text: string; order: number };

/**
 * Build a deterministic, punctuation-/accent-/case-insensitive searcher over a
 * list, matching the supplied text projection of each item.
 *
 * Deliberately NOT a fuzzy-scoring search (we previously used fuse.js and it
 * caused the picker list to reorder/flicker as scores shifted per keystroke).
 * Instead:
 *   - both query and candidate are normalised (see {@link normaliseForSearch}),
 *     so "Queens" matches "Queen's" and "st marys" matches "St Mary's";
 *   - a candidate matches when EVERY whitespace-separated query token is a
 *     substring of its normalised text (so word order and mid-name words work);
 *   - results are ranked deterministically — whole-query prefix matches first,
 *     then earliest match position, then shorter names, then the list's own
 *     order — so the ordering only ever narrows as the user types and never
 *     jitters up and down.
 *
 * The index is built once here, so callers should memoise the searcher on the
 * list (not per query) to avoid re-normalising on every keystroke.
 */
export const createPlaceSearch = <T>(
  items: readonly T[],
  getText: (item: T) => string,
): PlaceSearcher<T> => {
  const indexed: Indexed<T>[] = items.map((item, order) => ({
    item,
    text: normaliseForSearch(getText(item)),
    order,
  }));

  return (query, limit) => {
    const normalisedQuery = normaliseForSearch(query);
    if (normalisedQuery.length === 0) {
      return [];
    }
    const tokens = normalisedQuery.split(" ");
    const firstToken = tokens[0];
    if (firstToken === undefined) {
      return [];
    }

    const matches = indexed
      .filter((entry) => tokens.every((token) => entry.text.includes(token)))
      .map((entry) => ({
        entry,
        // 0 = the whole query is a prefix of the name (strongest), else 1.
        prefixRank: entry.text.startsWith(normalisedQuery) ? 0 : 1,
        // How early the first token appears — earlier is a better match.
        firstTokenPos: entry.text.indexOf(firstToken),
      }))
      .sort(
        (a, b) =>
          a.prefixRank - b.prefixRank ||
          a.firstTokenPos - b.firstTokenPos ||
          a.entry.text.length - b.entry.text.length ||
          a.entry.order - b.entry.order,
      );

    return matches.slice(0, limit).map((match) => match.entry.item);
  };
};
